import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api } from '../utils/api';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import FounderBadge from '../components/FounderBadge';
import {
  Heart, Trash2, Plus, ArrowLeft, Lock, MessageCircle, Send, Flag,
  Sparkles, BookOpen, HandHeart, Sun, EyeOff, Pin, PinOff, Star,
} from 'lucide-react';
import { toast } from 'sonner';

// Category metadata — icon + tint + label used across form, filters, and post cards
const CATEGORIES = [
  { key: 'founder_note',  label: 'Founder Note',   Icon: Star,     tint: '#B38A2E', bg: '#FBF3DC', adminOnly: true },
  { key: 'insight',       label: 'Insight',        Icon: Sparkles, tint: '#B77BB0', bg: '#F6ECF3' },
  { key: 'wisdom',        label: 'Wisdom',         Icon: BookOpen, tint: '#78A18E', bg: '#EBF3EE' },
  { key: 'prayer',        label: 'Prayer Request', Icon: HandHeart, tint: '#E57A44', bg: '#FFF0E5' },
  { key: 'encouragement', label: 'Encouragement',  Icon: Sun,      tint: '#B38A2E', bg: '#FBF3DC' },
];
const CAT_MAP = CATEGORIES.reduce((acc, c) => { acc[c.key] = c; return acc; }, {});

const REACTIONS = [
  { key: 'heart', label: 'Love',  Icon: Heart,     tint: '#E57A44' },
  { key: 'pray',  label: 'Pray',  Icon: HandHeart, tint: '#78A18E' },
  { key: 'amen',  label: 'Amen',  Icon: Sparkles,  tint: '#B38A2E' },
];

const authorStyle = (isAdmin) => ({
  fontFamily: 'Nunito, sans-serif',
  fontWeight: 700,
  color: isAdmin ? '#B38A2E' : '#5C715E',
});

export default function SanctuaryPrayerWall() {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [postType, setPostType] = useState('prayer');
  const [isAnonymous, setIsAnonymous] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [notMember, setNotMember] = useState(false);
  const [activeCategory, setActiveCategory] = useState('all');

  const [openComments, setOpenComments] = useState({});
  const [drafts, setDrafts] = useState({});
  const [draftAnon, setDraftAnon] = useState({});
  const [posting, setPosting] = useState({});

  const { user } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';

  useEffect(() => { load(activeCategory); }, [activeCategory]); // eslint-disable-line

  const load = async (cat = activeCategory) => {
    setLoading(true);
    try {
      const { data } = await api.get('/sanctuary/prayers', { params: { category: cat } });
      setPosts(data);
    } catch (err) {
      if (err?.response?.status === 402) setNotMember(true);
      else toast.error('Could not load the Fellowship Channel');
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!title.trim() || !description.trim()) { toast.error('Please fill in both fields'); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post('/sanctuary/prayers', {
        title: title.trim(),
        description: description.trim(),
        post_type: postType,
        is_anonymous: isAnonymous,
      });
      // Only prepend if it matches current filter
      if (activeCategory === 'all' || activeCategory === postType) {
        setPosts([data, ...posts]);
      }
      setTitle(''); setDescription(''); setIsAnonymous(false); setPostType('prayer'); setShowForm(false);
      const cat = CAT_MAP[postType]?.label || 'Post';
      toast.success(`${cat} shared with the circle`);
    } catch (err) {
      const detail = err?.response?.data?.detail || 'Could not share your post';
      toast.error(typeof detail === 'string' ? detail : 'Could not share');
    } finally {
      setSubmitting(false);
    }
  };

  const react = async (id, rt) => {
    // optimistic
    setPosts(prev => prev.map(p => {
      if (p.id !== id) return p;
      const cur = p.reactions?.[rt] || { count: 0, i_reacted: false };
      const next = { count: cur.count + (cur.i_reacted ? -1 : 1), i_reacted: !cur.i_reacted };
      const updated = { ...p, reactions: { ...(p.reactions || {}), [rt]: next } };
      if (rt === 'pray') { updated.i_prayed = next.i_reacted; updated.pray_count = next.count; }
      return updated;
    }));
    try {
      await api.post(`/sanctuary/prayers/${id}/react`, { reaction_type: rt });
    } catch (_) {
      toast.error('Could not update');
      load();
    }
  };

  const flag = async (id) => {
    if (!window.confirm('Flag this post for admin review? Members flag content they find inappropriate.')) return;
    try {
      const { data } = await api.post(`/sanctuary/prayers/${id}/flag`);
      setPosts(posts.map(p => p.id === id ? { ...p, flag_count: data.flag_count, i_flagged: true, hidden: data.hidden } : p));
      toast.success('Reported to admin. Thank you for keeping the circle safe.');
    } catch (_) {
      toast.error('Could not report');
    }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this post? This cannot be undone.')) return;
    try {
      await api.delete(`/sanctuary/prayers/${id}`);
      setPosts(posts.filter(p => p.id !== id));
    } catch (_) { toast.error('Could not delete'); }
  };

  const togglePin = async (id) => {
    try {
      const { data } = await api.post(`/admin/sanctuary/prayers/${id}/pin`);
      // reload so the sort order updates cleanly
      await load();
      toast.success(data.is_pinned ? 'Pinned to the top of the circle' : 'Unpinned');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not update pin');
    }
  };

  const toggleComments = (pid) => setOpenComments(prev => ({ ...prev, [pid]: !prev[pid] }));

  const postComment = async (pid) => {
    const text = (drafts[pid] || '').trim();
    if (!text) return;
    setPosting(prev => ({ ...prev, [pid]: true }));
    try {
      const { data: c } = await api.post(`/sanctuary/prayers/${pid}/comments`, {
        content: text,
        is_anonymous: !!draftAnon[pid],
      });
      setPosts(posts.map(p => p.id === pid
        ? { ...p, comments: [...(p.comments || []), c], comment_count: (p.comment_count || 0) + 1 }
        : p
      ));
      setDrafts(prev => ({ ...prev, [pid]: '' }));
      setDraftAnon(prev => ({ ...prev, [pid]: false }));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not post');
    } finally {
      setPosting(prev => ({ ...prev, [pid]: false }));
    }
  };

  const flagComment = async (pid, cid) => {
    if (!window.confirm('Flag this reply for admin review?')) return;
    try {
      const { data } = await api.post(`/sanctuary/prayers/${pid}/comments/${cid}/flag`);
      setPosts(posts.map(p => p.id === pid ? {
        ...p,
        comments: (p.comments || []).map(c => c.id === cid ? { ...c, flag_count: data.flag_count, i_flagged: true, hidden: data.hidden } : c),
      } : p));
      toast.success('Reported to admin.');
    } catch (_) { toast.error('Could not report'); }
  };

  const removeComment = async (pid, cid) => {
    try {
      await api.delete(`/sanctuary/prayers/${pid}/comments/${cid}`);
      setPosts(posts.map(p => p.id === pid
        ? { ...p, comments: (p.comments || []).filter(c => c.id !== cid), comment_count: Math.max(0, (p.comment_count || 1) - 1) }
        : p
      ));
    } catch (_) { toast.error('Could not delete'); }
  };

  const filteredCount = useMemo(() => posts.length, [posts]);

  if (notMember) {
    return (
      <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
        <Header />
        <div className="max-w-2xl mx-auto px-6 py-24 text-center">
          <div className="w-16 h-16 rounded-full mx-auto mb-5 flex items-center justify-center" style={{ backgroundColor: '#E8C26525' }}>
            <Lock className="w-8 h-8" style={{ color: '#B38A2E' }} />
          </div>
          <h1 className="text-3xl font-bold mb-3" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Sanctuary members only</h1>
          <p className="mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553', lineHeight: 1.7 }}>The Fellowship Channel is a private circle for members of The Sanctuary. Join for $6.99/month — cancel anytime.</p>
          <button onClick={() => navigate('/sanctuary')} className="px-6 py-3 rounded-full inline-flex items-center gap-2 hover:-translate-y-0.5 transition"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700 }} data-testid="sanctuary-locked-cta">
            Learn about The Sanctuary
          </button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: '#FDFBF7', minHeight: '100vh' }}>
      <Header />
      <div className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/sanctuary/dashboard" className="inline-flex items-center gap-2 text-sm mb-6" style={{ fontFamily: 'Nunito, sans-serif', color: '#E57A44', fontWeight: 600 }} data-testid="fellowship-back">
          <ArrowLeft className="w-4 h-4" /> Back to Sanctuary
        </Link>

        <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full mb-3" style={{ backgroundColor: '#FFF5E5', color: '#B38A2E', fontFamily: 'Manrope, sans-serif', fontSize: '11px', fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', border: '1px solid #E8C26555' }}>
              <Lock className="w-3 h-3" /> Members only
            </div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>Fellowship Channel</h1>
            <p style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>A private circle for insight, wisdom, prayer, and encouragement.</p>
          </div>
          <button
            onClick={() => setShowForm(!showForm)}
            className="px-5 py-2.5 rounded-full flex items-center gap-2 transition hover:-translate-y-0.5"
            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700 }}
            data-testid="fellowship-toggle-form"
          >
            <Plus className="w-4 h-4" />
            <span>{showForm ? 'Cancel' : 'Share with the circle'}</span>
          </button>
        </div>

        {/* Category filter tabs */}
        <div className="flex flex-wrap items-center gap-2 mb-6" data-testid="fellowship-filters">
          <FilterChip active={activeCategory === 'all'} onClick={() => setActiveCategory('all')} label="All" tint="#2C2724" testid="filter-all" />
          {CATEGORIES.map(c => (
            <FilterChip key={c.key}
              active={activeCategory === c.key}
              onClick={() => setActiveCategory(c.key)}
              label={c.label}
              Icon={c.Icon}
              tint={c.tint}
              testid={`filter-${c.key}`}
            />
          ))}        </div>

        {showForm && (
          <form onSubmit={submit} className="p-6 rounded-3xl mb-8" style={{ backgroundColor: '#FFFFFF', border: '1px solid #F0EBE0', boxShadow: '0 8px 30px rgba(0,0,0,0.03)' }} data-testid="fellowship-form">
            {/* Category picker */}
            <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>What are you sharing?</label>
            <div className="flex flex-wrap gap-2 mb-4">
              {CATEGORIES.filter(c => !c.adminOnly || isAdmin).map(c => (
                <button
                  key={c.key}
                  type="button"
                  onClick={() => setPostType(c.key)}
                  className="flex items-center gap-1.5 px-3 py-2 rounded-full text-sm transition"
                  style={{
                    fontFamily: 'Manrope, sans-serif',
                    backgroundColor: postType === c.key ? c.tint : c.bg,
                    color: postType === c.key ? '#FDFBF7' : c.tint,
                    border: `1.5px solid ${c.tint}`,
                    fontWeight: 700,
                  }}
                  data-testid={`fellowship-type-${c.key}`}
                >
                  <c.Icon className="w-3.5 h-3.5" />
                  <span>{c.label}</span>
                </button>
              ))}
            </div>

            <div className="mb-4">
              <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Title</label>
              <input value={title} onChange={(e) => setTitle(e.target.value)} maxLength={120} required
                placeholder="A short title (e.g., What God taught me this week)"
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Nunito, sans-serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="fellowship-input-title" />
            </div>
            <div className="mb-4">
              <label className="block mb-2 text-sm font-semibold" style={{ fontFamily: 'Manrope, sans-serif', color: '#2C2724' }}>Share what's on your heart</label>
              <textarea value={description} onChange={(e) => setDescription(e.target.value)} maxLength={2000} required rows={5}
                placeholder="Speak freely — this circle is private and prayerful."
                className="w-full px-4 py-3 rounded-xl border focus:outline-none focus:ring-2"
                style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                data-testid="fellowship-input-description" />
            </div>

            <div className="flex items-center justify-between flex-wrap gap-3">
              <label className="inline-flex items-center gap-2 cursor-pointer" data-testid="fellowship-anonymous-wrap">
                <input type="checkbox" checked={isAnonymous} onChange={(e) => setIsAnonymous(e.target.checked)}
                  className="w-4 h-4 rounded" style={{ accentColor: '#E57A44' }}
                  data-testid="fellowship-anonymous-checkbox" />
                <span className="text-sm" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>
                  Post anonymously
                </span>
              </label>
              <button type="submit" disabled={submitting}
                className="px-6 py-3 rounded-full transition hover:-translate-y-0.5 disabled:opacity-50"
                style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#E57A44', color: '#FDFBF7', fontWeight: 700 }}
                data-testid="fellowship-submit">
                {submitting ? 'Sharing…' : 'Share with the circle'}
              </button>
            </div>
          </form>
        )}

        {loading ? (
          <div className="text-center py-12"><div className="animate-spin rounded-full h-10 w-10 border-b-2 mx-auto" style={{ borderColor: '#E57A44' }} /></div>
        ) : filteredCount === 0 ? (
          <div className="p-10 rounded-3xl text-center" style={{ backgroundColor: '#FFFFFF', border: '1px dashed #E6E2D8' }} data-testid="fellowship-empty">
            <Sparkles className="w-10 h-10 mx-auto mb-3" style={{ color: '#E8C265' }} />
            <p style={{ fontFamily: 'Lora, serif', color: '#595553', fontStyle: 'italic' }}>The circle is quiet right now. Share the first thought.</p>
          </div>
        ) : (
          <ul className="space-y-4">
            {posts.map(p => {
              const cat = CAT_MAP[p.post_type] || CAT_MAP.prayer;
              const CatIcon = cat.Icon;
              const isOwner = p.user_id === user?._id || p.user_id === user?.id;
              const canDelete = isOwner || isAdmin;
              return (
                <li key={p.id}
                    className="p-6 rounded-3xl"
                    style={{
                      backgroundColor: p.post_type === 'founder_note' ? '#FFF8E5' : (p.is_pinned ? '#FFFBF3' : '#FFFFFF'),
                      border: p.post_type === 'founder_note' ? '2px solid #E8C265' : (p.is_pinned ? '1.5px solid #E8C265' : '1px solid #F0EBE0'),
                      boxShadow: p.post_type === 'founder_note' ? '0 10px 40px rgba(232, 194, 101, 0.25)' : (p.is_pinned ? '0 8px 30px rgba(232, 194, 101, 0.18)' : '0 4px 20px rgba(0,0,0,0.03)'),
                      opacity: p.hidden ? 0.75 : 1,
                    }}
                    data-testid={`fellowship-item-${p.id}`}>

                  {/* Pinned banner */}
                  {p.is_pinned && (
                    <div className="flex items-center gap-2 mb-3 -mt-1" data-testid={`fellowship-pinned-banner-${p.id}`}>
                      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ background: 'linear-gradient(135deg, #F3D97C 0%, #E8C265 100%)', color: '#3B2F14', fontFamily: 'Manrope, sans-serif', fontSize: '10px', fontWeight: 800, letterSpacing: '0.1em', textTransform: 'uppercase', border: '1px solid #D9B14A' }}>
                        <Pin className="w-3 h-3" fill="#3B2F14" strokeWidth={0} /> PINNED BY GENESIS
                      </span>
                    </div>
                  )}

                  {/* Header: category chip + hidden banner */}
                  <div className="flex items-center justify-between gap-3 mb-3 flex-wrap">
                    <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full" style={{ backgroundColor: cat.bg, color: cat.tint, fontFamily: 'Manrope, sans-serif', fontSize: '10.5px', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', border: `1px solid ${cat.tint}30` }} data-testid={`fellowship-item-cat-${p.id}`}>
                      <CatIcon className="w-3 h-3" /> {cat.label}
                    </span>
                    {p.hidden && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs" style={{ backgroundColor: '#F3E5DB', color: '#B34E1F', fontFamily: 'Manrope, sans-serif', fontWeight: 700 }} data-testid={`fellowship-hidden-${p.id}`}>
                        <EyeOff className="w-3 h-3" /> Hidden — awaiting review
                      </span>
                    )}
                  </div>

                  <div className="flex items-start justify-between gap-3 mb-2">
                    <h3 className="text-lg font-bold" style={{ fontFamily: 'Merriweather, serif', color: '#2C2A29' }}>{p.title}</h3>
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {isAdmin && (
                        <button onClick={() => togglePin(p.id)}
                          title={p.is_pinned ? 'Unpin from top' : 'Pin as Founder Note'}
                          className="text-xs opacity-40 hover:opacity-100 transition"
                          data-testid={`fellowship-pin-${p.id}`}>
                          {p.is_pinned
                            ? <PinOff className="w-4 h-4" style={{ color: '#B38A2E' }} />
                            : <Pin className="w-4 h-4" style={{ color: '#8C867D' }} />}
                        </button>
                      )}
                      {canDelete && (
                        <button onClick={() => remove(p.id)} title="Delete" className="text-xs opacity-40 hover:opacity-100 transition" data-testid={`fellowship-delete-${p.id}`}>
                          <Trash2 className="w-4 h-4" style={{ color: '#8C867D' }} />
                        </button>
                      )}
                    </div>
                  </div>
                  <p className="text-sm mb-4 whitespace-pre-wrap" style={{ fontFamily: 'Lora, serif', color: '#595553', lineHeight: 1.8 }}>{p.description}</p>

                  {/* Author row */}
                  <div className="flex items-center gap-2 flex-wrap mb-4 text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                    <span style={authorStyle(p.is_admin)}>{p.display_name || 'A member'}</span>
                    {p.is_admin && <FounderBadge compact />}
                    <span>· {new Date(p.created_at).toLocaleDateString()}</span>
                    {!isOwner && !p.i_flagged && (
                      <button onClick={() => flag(p.id)} className="ml-auto inline-flex items-center gap-1 opacity-60 hover:opacity-100 transition" style={{ color: '#8C867D' }} data-testid={`fellowship-flag-${p.id}`} title="Report to admin">
                        <Flag className="w-3.5 h-3.5" /> <span>Flag</span>
                      </button>
                    )}
                    {p.i_flagged && (
                      <span className="ml-auto inline-flex items-center gap-1 text-xs" style={{ color: '#B34E1F' }}>
                        <Flag className="w-3.5 h-3.5" /> Reported
                      </span>
                    )}
                  </div>

                  {/* Reactions + comments toggle */}
                  <div className="flex items-center gap-2 flex-wrap">
                    {REACTIONS.map(r => {
                      const info = p.reactions?.[r.key] || { count: 0, i_reacted: false };
                      return (
                        <button key={r.key}
                          onClick={() => react(p.id, r.key)}
                          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition hover:-translate-y-0.5"
                          style={{
                            fontFamily: 'Manrope, sans-serif',
                            backgroundColor: info.i_reacted ? r.tint : '#FDFBF7',
                            color: info.i_reacted ? '#FFFFFF' : r.tint,
                            border: `1.5px solid ${r.tint}`,
                            fontWeight: 600,
                            fontSize: '13px',
                          }}
                          data-testid={`fellowship-react-${r.key}-${p.id}`}>
                          <r.Icon className="w-3.5 h-3.5" fill={info.i_reacted ? '#FFFFFF' : 'none'} />
                          <span>{r.label}</span>
                          {info.count > 0 && <span className="text-xs opacity-80">({info.count})</span>}
                        </button>
                      );
                    })}
                    <button onClick={() => toggleComments(p.id)}
                      className="ml-auto flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition hover:-translate-y-0.5"
                      style={{
                        fontFamily: 'Manrope, sans-serif',
                        backgroundColor: openComments[p.id] ? '#5B7C99' : '#FDFBF7',
                        color: openComments[p.id] ? '#FFFFFF' : '#5B7C99',
                        border: '1.5px solid #5B7C99', fontWeight: 600, fontSize: '13px',
                      }}
                      data-testid={`fellowship-comments-toggle-${p.id}`}>
                      <MessageCircle className="w-3.5 h-3.5" />
                      <span>Reply</span>
                      {(p.comment_count || 0) > 0 && <span className="text-xs opacity-80">({p.comment_count})</span>}
                    </button>
                  </div>

                  {openComments[p.id] && (
                    <div className="mt-4 pt-4" style={{ borderTop: '1px dashed #E6E2D8' }} data-testid={`fellowship-comments-panel-${p.id}`}>
                      {(p.comments || []).length === 0 && (
                        <p className="text-xs mb-3" style={{ fontFamily: 'Lora, serif', color: '#8C867D', fontStyle: 'italic' }}>Be the first to reply.</p>
                      )}
                      <ul className="space-y-2 mb-3">
                        {(p.comments || []).map(c => (
                          <li key={c.id} className="p-3 rounded-2xl flex items-start gap-3" style={{ backgroundColor: c.is_admin ? '#FFF5E5' : '#F8F5EE', border: c.is_admin ? '1px solid #E8C26540' : '1px solid #F0EBE0', opacity: c.hidden ? 0.6 : 1 }} data-testid={`fellowship-comment-${c.id}`}>
                            <div className="flex-1">
                              <p className="text-sm whitespace-pre-wrap" style={{ fontFamily: 'Lora, serif', color: '#2C2A29', lineHeight: 1.6 }}>{c.content}</p>
                              <div className="flex items-center gap-2 mt-1 text-xs flex-wrap" style={{ fontFamily: 'Nunito, sans-serif', color: '#8C867D' }}>
                                <span style={authorStyle(c.is_admin)}>{c.display_name || c.user_name}</span>
                                {c.is_admin && <FounderBadge compact />}
                                <span>· {new Date(c.created_at).toLocaleDateString()}</span>
                                {c.hidden && (
                                  <span className="inline-flex items-center gap-1" style={{ color: '#B34E1F' }}>
                                    <EyeOff className="w-3 h-3" /> Hidden
                                  </span>
                                )}
                              </div>
                            </div>
                            {!(c.user_id === user?._id || c.user_id === user?.id) && !c.i_flagged && (
                              <button onClick={() => flagComment(p.id, c.id)} title="Report" className="opacity-40 hover:opacity-100 transition flex-shrink-0" data-testid={`fellowship-comment-flag-${c.id}`}>
                                <Flag className="w-3.5 h-3.5" style={{ color: '#8C867D' }} />
                              </button>
                            )}
                            {(c.user_id === user?._id || c.user_id === user?.id || isAdmin) && (
                              <button onClick={() => removeComment(p.id, c.id)} title="Delete" className="opacity-40 hover:opacity-100 transition flex-shrink-0" data-testid={`fellowship-comment-delete-${c.id}`}>
                                <Trash2 className="w-3.5 h-3.5" style={{ color: '#8C867D' }} />
                              </button>
                            )}
                          </li>
                        ))}
                      </ul>
                      <div className="flex flex-col gap-2">
                        <textarea
                          value={drafts[p.id] || ''}
                          onChange={(e) => setDrafts(prev => ({ ...prev, [p.id]: e.target.value }))}
                          onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); postComment(p.id); } }}
                          maxLength={500}
                          rows={2}
                          placeholder="Share encouragement, a scripture, or a prayer…"
                          className="w-full px-3 py-2 rounded-xl border text-sm focus:outline-none focus:ring-2"
                          style={{ fontFamily: 'Lora, serif', backgroundColor: '#FDFBF7', borderColor: '#E6E2D8', color: '#2C2724' }}
                          data-testid={`fellowship-comment-input-${p.id}`}
                        />
                        <div className="flex items-center justify-between gap-2 flex-wrap">
                          <label className="inline-flex items-center gap-2 cursor-pointer">
                            <input type="checkbox"
                              checked={!!draftAnon[p.id]}
                              onChange={(e) => setDraftAnon(prev => ({ ...prev, [p.id]: e.target.checked }))}
                              className="w-3.5 h-3.5" style={{ accentColor: '#E57A44' }}
                              data-testid={`fellowship-comment-anon-${p.id}`} />
                            <span className="text-xs" style={{ fontFamily: 'Nunito, sans-serif', color: '#595553' }}>Reply anonymously</span>
                          </label>
                          <button
                            onClick={() => postComment(p.id)}
                            disabled={posting[p.id] || !(drafts[p.id] || '').trim()}
                            className="px-3 py-2 rounded-xl flex items-center gap-1 transition disabled:opacity-50 hover:-translate-y-0.5"
                            style={{ fontFamily: 'Manrope, sans-serif', backgroundColor: '#5B7C99', color: '#FFFFFF', fontWeight: 600, fontSize: '13px' }}
                            data-testid={`fellowship-comment-submit-${p.id}`}
                          >
                            <Send className="w-3.5 h-3.5" />
                            <span>Send</span>
                          </button>
                        </div>
                      </div>
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
      <Footer />
    </div>
  );
}

function FilterChip({ active, onClick, label, Icon, tint, testid }) {
  return (
    <button onClick={onClick}
      className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm transition"
      style={{
        fontFamily: 'Manrope, sans-serif',
        backgroundColor: active ? tint : '#FFFFFF',
        color: active ? '#FFFFFF' : tint,
        border: `1.5px solid ${tint}`,
        fontWeight: 700,
      }}
      data-testid={testid}
    >
      {Icon && <Icon className="w-3.5 h-3.5" />}
      <span>{label}</span>
    </button>
  );
}
